import React, { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { PageHeader, StatusBadge } from "@/components/Common";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Plus, DotsThreeVertical, PencilSimple, Trash, Eye, ArrowUUpLeft, Certificate, Printer, X } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

const STAGES = ["RawMaterial", "Cooking", "Chilling", "FreezeDrying", "Packaging", "FinishedProduct"];
const RESULTS = ["Passed", "Failed", "Pending"];
const KINDS = ["number", "text", "select", "boolean"];

// ============== QC Record Editor ==============
const EMPTY_QC = { batch_id: "", batch_no: "", stage: "RawMaterial", result: "Passed",
                    moisture: "", weight: "", appearance: "", taste: "", seal_quality: "",
                    rejected_qty: 0, notes: "", template_id: "", checks: [] };

function QCRecordEditor({ open, mode, initial, batches, templates, onClose, onDone }) {
  const [form, setForm] = useState(EMPTY_QC);
  const [suggested, setSuggested] = useState([]);  // templates ranked for the selected batch+stage
  useEffect(() => {
    if (open) {
      const seed = initial ? { ...EMPTY_QC, ...initial,
                                 moisture: initial.moisture ?? "",
                                 weight: initial.weight ?? "",
                                 checks: initial.checks || [] } : { ...EMPTY_QC };
      setForm(seed);
      setSuggested([]);
    }
  }, [open, initial]);
  const readOnly = mode === "view";

  // Auto-fetch template suggestions when batch/stage changes
  useEffect(() => {
    if (!open || !form.batch_id || readOnly) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/qc-templates/for-batch/${form.batch_id}`,
                                        { params: { stage: form.stage } });
        if (cancelled) return;
        setSuggested(data);
        // Auto-apply if exactly one SKU-specific template and user hasn't picked one yet
        const sku_specific = data.filter(t => t._recommended);
        if (sku_specific.length === 1 && !form.template_id) {
          const t = sku_specific[0];
          const checks = (t.checkpoints || []).map(c => ({
            key: c.key, label: c.label, kind: c.kind, unit: c.unit, min: c.min, max: c.max,
            required: c.required, options: c.options, value: "",
          }));
          setForm(f => ({ ...f, template_id: t.id, checks }));
          toast.info(`Applied template: ${t.name}`, { duration: 2000 });
        }
      } catch (e) { /* silent */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, form.batch_id, form.stage]);

  // Template handling
  const template = templates.find(t => t.id === form.template_id);

  const applyTemplate = (tid) => {
    const t = templates.find(x => x.id === tid);
    if (!t) { setForm({ ...form, template_id: "", checks: [] }); return; }
    const checks = (t.checkpoints || []).map(c => ({
      key: c.key, label: c.label, kind: c.kind, unit: c.unit, min: c.min, max: c.max,
      required: c.required, options: c.options, value: "",
    }));
    setForm({ ...form, template_id: tid, stage: t.stage || form.stage, checks });
  };

  const updCheck = (i, val) => {
    const c = [...form.checks]; c[i].value = val; setForm({ ...form, checks: c });
  };

  const submit = async () => {
    try {
      const body = { ...form,
        moisture: form.moisture !== "" ? parseFloat(form.moisture) : null,
        weight: form.weight !== "" ? parseFloat(form.weight) : null,
        rejected_qty: parseFloat(form.rejected_qty) || 0,
      };
      if (mode === "edit") {
        await api.put(`/qc/${initial.id}`, body);
        toast.success("QC updated");
      } else {
        await api.post("/qc", body);
        toast.success("QC recorded");
      }
      onDone?.(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const title = { edit: "Edit QC Record", view: "QC Record Details", create: "Record QC Check" }[mode] || "QC";

  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>{title}</DialogTitle><DialogDescription className="sr-only">{title}</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label>Batch</Label>
            <Select value={form.batch_id} onValueChange={v => {
              const b = batches.find(x => x.id === v);
              setForm({ ...form, batch_id: v, batch_no: b?.batch_no || "" });
            }} disabled={readOnly || mode === "edit"}>
              <SelectTrigger data-testid="qc-batch"><SelectValue placeholder="Select batch"/></SelectTrigger>
              <SelectContent>{batches.map(b => <SelectItem key={b.id} value={b.id}>{b.batch_no} · {b.sku_name}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div className="col-span-2">
            <Label>Template {suggested.some(t => t._recommended) && <span className="ml-1 text-[10px] font-semibold uppercase tracking-wide text-emerald-700">★ Recommended available</span>}</Label>
            <Select value={form.template_id || "__none__"} onValueChange={v => applyTemplate(v === "__none__" ? "" : v)} disabled={readOnly}>
              <SelectTrigger data-testid="qc-template"><SelectValue placeholder="No template"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">No template</SelectItem>
                {/* Recommended templates first (SKU-specific) */}
                {suggested.filter(t => t._recommended).map(t =>
                  <SelectItem key={t.id} value={t.id}>★ {t.name} · {t.stage} <span className="text-[10px] text-emerald-700 ml-1">(for this SKU)</span></SelectItem>
                )}
                {suggested.filter(t => !t._recommended).map(t =>
                  <SelectItem key={t.id} value={t.id}>{t.name} · {t.stage}</SelectItem>
                )}
                {/* Fallback: show templates not in suggestions (different stage) */}
                {templates.filter(t => t.status !== "deleted" && !suggested.some(s => s.id === t.id)).map(t =>
                  <SelectItem key={t.id} value={t.id}>{t.name} · {t.stage}</SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>

          <div><Label>Stage</Label>
            <Select value={form.stage} onValueChange={v => setForm({ ...form, stage: v })} disabled={readOnly}>
              <SelectTrigger><SelectValue/></SelectTrigger>
              <SelectContent>{STAGES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Result</Label>
            <Select value={form.result} onValueChange={v => setForm({ ...form, result: v })} disabled={readOnly}>
              <SelectTrigger data-testid="qc-result"><SelectValue/></SelectTrigger>
              <SelectContent>{RESULTS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          {/* Dynamic checkpoints from template */}
          {form.checks?.length > 0 && (
            <div className="col-span-2 space-y-2 border rounded-md p-3 bg-muted/20">
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Template: {template?.name}</div>
              {form.checks.map((c, i) => (
                <div key={i} className="grid grid-cols-3 gap-2 items-center">
                  <Label className="col-span-1 text-xs">{c.label}{c.required && <span className="text-red-500">*</span>}{c.unit ? ` (${c.unit})` : ""}</Label>
                  <div className="col-span-2">
                    {c.kind === "boolean" ? (
                      <Select value={String(c.value)} onValueChange={v => updCheck(i, v === "true")} disabled={readOnly}>
                        <SelectTrigger><SelectValue placeholder="Yes/No"/></SelectTrigger>
                        <SelectContent><SelectItem value="true">Yes</SelectItem><SelectItem value="false">No</SelectItem></SelectContent>
                      </Select>
                    ) : c.kind === "select" ? (
                      <Select value={c.value || ""} onValueChange={v => updCheck(i, v)} disabled={readOnly}>
                        <SelectTrigger><SelectValue placeholder="Select"/></SelectTrigger>
                        <SelectContent>{(c.options || []).map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                      </Select>
                    ) : (
                      <Input type={c.kind === "number" ? "number" : "text"} value={c.value ?? ""} onChange={e => updCheck(i, e.target.value)} disabled={readOnly}/>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div><Label>Moisture %</Label><Input type="number" value={form.moisture} onChange={e => setForm({ ...form, moisture: e.target.value })} disabled={readOnly}/></div>
          <div><Label>Weight (kg)</Label><Input type="number" value={form.weight} onChange={e => setForm({ ...form, weight: e.target.value })} disabled={readOnly}/></div>
          <div><Label>Appearance</Label><Input value={form.appearance} onChange={e => setForm({ ...form, appearance: e.target.value })} disabled={readOnly}/></div>
          <div><Label>Taste</Label><Input value={form.taste} onChange={e => setForm({ ...form, taste: e.target.value })} disabled={readOnly}/></div>
          <div><Label>Seal Quality</Label><Input value={form.seal_quality} onChange={e => setForm({ ...form, seal_quality: e.target.value })} disabled={readOnly}/></div>
          <div><Label>Rejected Qty</Label><Input type="number" value={form.rejected_qty} onChange={e => setForm({ ...form, rejected_qty: e.target.value })} disabled={readOnly}/></div>
          <div className="col-span-2"><Label>Notes / CAPA</Label><Textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} disabled={readOnly}/></div>
        </div>
        <DialogFooter>
          {readOnly ? <Button variant="outline" onClick={onClose}>Close</Button> : (
            <>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button onClick={submit} disabled={!form.batch_id} data-testid="qc-save">
                {mode === "edit" ? "Save Changes" : "Record"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============== Template Editor ==============
const EMPTY_TEMPLATE = { name: "", stage: "RawMaterial", description: "", checkpoints: [], sku_ids: [] };
const EMPTY_CP = () => ({ key: "", label: "", kind: "number", unit: "", min: "", max: "", options: [], required: false });

function TemplateEditor({ open, mode, initial, skus, onClose, onDone }) {
  const [form, setForm] = useState(EMPTY_TEMPLATE);
  useEffect(() => {
    if (open) setForm(initial ? {
      ...EMPTY_TEMPLATE, ...initial,
      checkpoints: initial.checkpoints || [],
      sku_ids: initial.sku_ids || (initial.sku_id ? [initial.sku_id] : []),
    } : { ...EMPTY_TEMPLATE });
  }, [open, initial]);
  const readOnly = mode === "view";

  const toggleSku = (id) => {
    const set = new Set(form.sku_ids);
    set.has(id) ? set.delete(id) : set.add(id);
    setForm({ ...form, sku_ids: Array.from(set) });
  };

  const addCP = () => setForm({ ...form, checkpoints: [...form.checkpoints, EMPTY_CP()] });
  const updCP = (i, k, v) => {
    const c = [...form.checkpoints]; c[i] = { ...c[i], [k]: v };
    setForm({ ...form, checkpoints: c });
  };
  const rmCP = (i) => setForm({ ...form, checkpoints: form.checkpoints.filter((_, j) => j !== i) });

  const submit = async () => {
    try {
      const body = { ...form,
        checkpoints: form.checkpoints.map((c, i) => ({
          ...c, step_no: i + 1,
          min: c.min === "" ? null : Number(c.min),
          max: c.max === "" ? null : Number(c.max),
          options: typeof c.options === "string"
            ? c.options.split(",").map(s => s.trim()).filter(Boolean)
            : c.options,
        }))
      };
      if (mode === "edit") {
        await api.put(`/qc-templates/${initial.id}`, body);
        toast.success("Template updated");
      } else {
        await api.post("/qc-templates", body);
        toast.success("Template created");
      }
      onDone?.(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const title = { edit: "Edit QC Template", view: "Template Details", create: "New QC Template" }[mode] || "Template";

  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader><DialogTitle>{title}</DialogTitle><DialogDescription className="sr-only">{title}</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Template Name</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} disabled={readOnly} data-testid="tpl-name"/></div>
          <div><Label>Stage</Label>
            <Select value={form.stage} onValueChange={v => setForm({ ...form, stage: v })} disabled={readOnly}>
              <SelectTrigger data-testid="tpl-stage"><SelectValue/></SelectTrigger>
              <SelectContent>{STAGES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="col-span-2"><Label>Description</Label><Textarea rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} disabled={readOnly}/></div>
          <div className="col-span-2">
            <Label>Applies to SKUs {form.sku_ids.length === 0 && <span className="ml-1 text-[10px] text-muted-foreground">(empty = applies to all SKUs)</span>}</Label>
            <div className="border rounded-md p-2 max-h-32 overflow-y-auto bg-muted/20" data-testid="tpl-sku-picker">
              {(skus || []).length === 0 && <div className="text-xs text-muted-foreground p-2">No SKUs loaded.</div>}
              <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                {(skus || []).map(s => (
                  <label key={s.id} className="flex items-center gap-2 text-xs cursor-pointer">
                    <input type="checkbox" checked={form.sku_ids.includes(s.id)}
                            onChange={() => toggleSku(s.id)} disabled={readOnly}/>
                    <span className="mono text-muted-foreground">{s.code}</span>
                    <span className="truncate">{s.name}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4">
          <div className="flex justify-between items-center mb-2">
            <Label>Checkpoints</Label>
            {!readOnly && <Button size="sm" variant="outline" onClick={addCP} data-testid="tpl-add-checkpoint"><Plus size={14} className="mr-1"/>Add Checkpoint</Button>}
          </div>
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {form.checkpoints.length === 0 && <div className="text-xs text-muted-foreground text-center py-6">No checkpoints yet — click "Add Checkpoint" to add moisture, weight, appearance, etc.</div>}
            {form.checkpoints.map((c, i) => (
              <div key={i} className="border rounded-md p-3 bg-muted/20">
                <div className="grid grid-cols-12 gap-2 mb-2">
                  <div className="col-span-3"><Label className="text-xs">Key</Label><Input value={c.key} onChange={e => updCP(i, "key", e.target.value)} placeholder="moisture" disabled={readOnly}/></div>
                  <div className="col-span-4"><Label className="text-xs">Label</Label><Input value={c.label} onChange={e => updCP(i, "label", e.target.value)} placeholder="Moisture %" disabled={readOnly}/></div>
                  <div className="col-span-2"><Label className="text-xs">Kind</Label>
                    <Select value={c.kind} onValueChange={v => updCP(i, "kind", v)} disabled={readOnly}>
                      <SelectTrigger><SelectValue/></SelectTrigger>
                      <SelectContent>{KINDS.map(k => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2"><Label className="text-xs">Unit</Label><Input value={c.unit} onChange={e => updCP(i, "unit", e.target.value)} placeholder="%, kg" disabled={readOnly}/></div>
                  <div className="col-span-1 flex items-end justify-end">
                    {!readOnly && <button onClick={() => rmCP(i)} className="text-red-600 hover:text-red-700 pb-2"><Trash size={16}/></button>}
                  </div>
                </div>
                {c.kind === "number" && (
                  <div className="grid grid-cols-3 gap-2">
                    <div><Label className="text-xs">Min</Label><Input type="number" value={c.min ?? ""} onChange={e => updCP(i, "min", e.target.value)} disabled={readOnly}/></div>
                    <div><Label className="text-xs">Max</Label><Input type="number" value={c.max ?? ""} onChange={e => updCP(i, "max", e.target.value)} disabled={readOnly}/></div>
                    <div className="flex items-end gap-2 pb-2">
                      <input type="checkbox" checked={c.required} onChange={e => updCP(i, "required", e.target.checked)} disabled={readOnly}/>
                      <span className="text-xs">Required</span>
                    </div>
                  </div>
                )}
                {c.kind === "select" && (
                  <div><Label className="text-xs">Options (comma-separated)</Label>
                    <Input value={Array.isArray(c.options) ? c.options.join(", ") : c.options}
                            onChange={e => updCP(i, "options", e.target.value)} placeholder="OK, Marginal, Reject" disabled={readOnly}/>
                  </div>
                )}
                {(c.kind === "text" || c.kind === "boolean") && (
                  <div className="flex items-center gap-2 pt-1">
                    <input type="checkbox" checked={c.required} onChange={e => updCP(i, "required", e.target.checked)} disabled={readOnly}/>
                    <span className="text-xs">Required</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <DialogFooter>
          {readOnly ? <Button variant="outline" onClick={onClose}>Close</Button> : (
            <>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button onClick={submit} disabled={!form.name} data-testid="tpl-save">
                {mode === "edit" ? "Save Changes" : "Create Template"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============== Certificate Dialog ==============
function CertificateDialog({ open, qc, onClose }) {
  const [cert, setCert] = useState(null);
  const ref = useRef();

  useEffect(() => {
    if (!open || !qc) { setCert(null); return; }
    (async () => {
      try {
        const { data } = await api.get(`/qc/${qc.id}/certificate`);
        setCert(data);
      } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    })();
  }, [open, qc]);

  const print = () => {
    if (!ref.current) return;
    const w = window.open("", "_blank", "width=900,height=1000");
    w.document.write(`<html><head><title>${cert.certificate_no}</title>
      <style>
        body{font-family:-apple-system,'Helvetica Neue',Arial,sans-serif;padding:32px;color:#111;}
        h1{font-size:22px;margin:0 0 4px;}
        h2{font-size:14px;color:#666;margin:0 0 24px;font-weight:400;}
        .brand{border-bottom:2px solid #111;padding-bottom:12px;margin-bottom:20px;}
        .kv{display:grid;grid-template-columns:180px 1fr;gap:8px 16px;margin:8px 0 24px;font-size:13px;}
        .kv b{color:#111;} .kv span{color:#333;}
        .section{margin-bottom:20px;}
        .section-title{font-size:11px;text-transform:uppercase;letter-spacing:1.5px;color:#666;font-weight:700;margin-bottom:8px;border-bottom:1px solid #ddd;padding-bottom:4px;}
        .verdict{display:inline-block;padding:8px 20px;font-weight:700;letter-spacing:0.5px;border-radius:4px;font-size:14px;}
        .pass{background:#dcfce7;color:#166534;} .fail{background:#fee2e2;color:#991b1b;} .pending{background:#fef3c7;color:#92400e;}
        .footer{margin-top:60px;display:grid;grid-template-columns:1fr 1fr;gap:32px;font-size:12px;}
        .sig{border-top:1px solid #111;padding-top:6px;margin-top:44px;}
        table{width:100%;border-collapse:collapse;font-size:12px;margin-top:4px;}
        th,td{border:1px solid #ddd;padding:6px 8px;text-align:left;}
        th{background:#f5f5f5;font-weight:700;}
      </style></head><body>${ref.current.innerHTML}</body></html>`);
    w.document.close(); w.focus();
    setTimeout(() => { w.print(); }, 300);
  };

  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>QC Certificate</DialogTitle>
          <DialogDescription className="sr-only">QC Certificate preview</DialogDescription>
        </DialogHeader>
        {!cert ? (
          <div className="py-8 text-center text-muted-foreground text-sm">Loading certificate…</div>
        ) : (
          <div ref={ref}>
            <div className="brand">
              <h1>Chericious Foods Pvt. Ltd.</h1>
              <h2>Certificate of Quality — {cert.certificate_no}</h2>
            </div>
            <div className="kv">
              <b>Batch No</b><span>{cert.batch?.batch_no}</span>
              <b>SKU</b><span>{cert.batch?.sku_name} <span className="mono" style={{color:"#666"}}>({cert.batch?.sku_code})</span></span>
              <b>Stage Inspected</b><span>{cert.qc?.stage}</span>
              <b>Manufactured</b><span>{cert.batch?.mfg_date ? new Date(cert.batch.mfg_date).toLocaleDateString() : "—"}</span>
              <b>Expiry</b><span>{cert.batch?.exp_date ? new Date(cert.batch.exp_date).toLocaleDateString() : "—"}</span>
              <b>Machine</b><span>{cert.batch?.machine_name || "—"}</span>
              <b>Issued</b><span>{new Date(cert.issued_at).toLocaleString()}</span>
              <b>Inspector</b><span>{cert.inspector}</span>
              <b>Verdict</b><span><span className={`verdict ${cert.verdict === "Passed" ? "pass" : cert.verdict === "Failed" ? "fail" : "pending"}`}>{cert.verdict?.toUpperCase()}</span></span>
            </div>

            <div className="section">
              <div className="section-title">Quality Parameters</div>
              <table>
                <thead><tr><th>Parameter</th><th>Value</th></tr></thead>
                <tbody>
                  {cert.qc?.moisture != null && <tr><td>Moisture</td><td>{cert.qc.moisture} %</td></tr>}
                  {cert.qc?.weight != null && <tr><td>Weight</td><td>{cert.qc.weight} kg</td></tr>}
                  {cert.qc?.appearance && <tr><td>Appearance</td><td>{cert.qc.appearance}</td></tr>}
                  {cert.qc?.taste && <tr><td>Taste</td><td>{cert.qc.taste}</td></tr>}
                  {cert.qc?.seal_quality && <tr><td>Seal Quality</td><td>{cert.qc.seal_quality}</td></tr>}
                  {(cert.qc?.checks || []).map((c, i) => (
                    <tr key={i}><td>{c.label}</td><td>{String(c.value ?? "—")} {c.unit || ""}</td></tr>
                  ))}
                  {cert.qc?.rejected_qty > 0 && <tr><td>Rejected Qty</td><td>{cert.qc.rejected_qty}</td></tr>}
                </tbody>
              </table>
            </div>

            {cert.qc?.notes && (
              <div className="section">
                <div className="section-title">Notes / CAPA</div>
                <p style={{fontSize:12, whiteSpace:"pre-wrap"}}>{cert.qc.notes}</p>
              </div>
            )}

            <div className="footer">
              <div>
                <div className="sig">Quality Inspector — {cert.inspector}</div>
              </div>
              <div>
                <div className="sig">QC Manager</div>
              </div>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={print} disabled={!cert} data-testid="cert-print">
            <Printer size={14} className="mr-1"/>Print Certificate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============== Page ==============
export default function QC() {
  const { user } = useAuth();
  const role = user?.role;
  const canManage = ["qc", "admin"].includes(role);

  const [qcs, setQcs] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [batches, setBatches] = useState([]);
  const [skus, setSkus] = useState([]);
  const [showDeleted, setShowDeleted] = useState(false);

  const [qcMode, setQcMode] = useState(null);
  const [qcSel, setQcSel] = useState(null);
  const [tplMode, setTplMode] = useState(null);
  const [tplSel, setTplSel] = useState(null);
  const [certQc, setCertQc] = useState(null);

  const load = async () => {
    setQcs((await api.get("/qc", { params: { include_deleted: showDeleted } })).data);
    setTemplates((await api.get("/qc-templates", { params: { include_inactive: showDeleted } })).data);
    setBatches((await api.get("/batches")).data);
    setSkus((await api.get("/skus")).data);
  };
  useEffect(() => { load(); }, [showDeleted]);  // eslint-disable-line

  const deleteQc = async (q) => {
    if (!window.confirm(`Delete QC record for batch ${q.batch_no}? It will be archived and can be restored.`)) return;
    try { await api.delete(`/qc/${q.id}`); toast.success("QC record archived"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const restoreQc = async (q) => {
    try { await api.post(`/qc/${q.id}/restore`); toast.success("QC restored"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const deleteTpl = async (t) => {
    if (!window.confirm(`Archive template "${t.name}"?`)) return;
    try { await api.delete(`/qc-templates/${t.id}`); toast.success("Template archived"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const restoreTpl = async (t) => {
    try { await api.post(`/qc-templates/${t.id}/restore`); toast.success("Template restored"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  return (
    <div data-testid="qc-page">
      <PageHeader subtitle="Quality Control" title="QC Checkpoints"
        actions={
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-1.5 text-xs font-semibold cursor-pointer whitespace-nowrap">
              <input type="checkbox" checked={showDeleted} onChange={e => setShowDeleted(e.target.checked)} data-testid="qc-show-deleted"/>
              Show archived
            </label>
            {canManage && (
              <Button onClick={() => { setQcSel(null); setQcMode("create"); }} data-testid="new-qc-btn">
                <Plus size={16} className="mr-1.5"/>Record QC
              </Button>
            )}
          </div>
        }
      />

      <Tabs defaultValue="records">
        <TabsList>
          <TabsTrigger value="records" data-testid="tab-qc-records">QC Records</TabsTrigger>
          <TabsTrigger value="templates" data-testid="tab-qc-templates">Templates</TabsTrigger>
        </TabsList>

        <TabsContent value="records">
          <Card className="shadow-none">
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Batch</TableHead><TableHead>Stage</TableHead>
                    <TableHead>Result</TableHead>
                    <TableHead className="text-right">Moisture %</TableHead>
                    <TableHead className="text-right">Weight</TableHead>
                    <TableHead>Notes</TableHead>
                    <TableHead>Inspector</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {qcs.map(q => {
                    const deleted = !!q.deleted_at;
                    return (
                      <TableRow key={q.id} className={deleted ? "opacity-60" : ""} data-testid={`qc-row-${q.batch_no || q.id}`}>
                        <TableCell className="mono font-semibold">
                          {q.batch_no}
                          {deleted && <Badge variant="secondary" className="ml-1.5 uppercase text-[9px]">archived</Badge>}
                        </TableCell>
                        <TableCell>{q.stage}</TableCell>
                        <TableCell><StatusBadge status={q.result}/></TableCell>
                        <TableCell className="text-right mono">{q.moisture ?? "—"}</TableCell>
                        <TableCell className="text-right mono">{q.weight ?? "—"}</TableCell>
                        <TableCell className="text-xs text-muted-foreground max-w-xs truncate">{q.notes || "—"}</TableCell>
                        <TableCell className="text-xs">{q.inspector || "—"}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{new Date(q.created_at).toLocaleDateString()}</TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button size="sm" variant="ghost" className="h-8 w-8 p-0" data-testid={`qc-actions-${q.batch_no || q.id}`}>
                                <DotsThreeVertical size={16} weight="bold"/>
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onSelect={() => { setQcSel(q); setQcMode("view"); }}>
                                <Eye size={14} className="mr-2"/>View
                              </DropdownMenuItem>
                              {canManage && !deleted && (
                                <DropdownMenuItem onSelect={() => { setQcSel(q); setQcMode("edit"); }} data-testid={`qc-edit-${q.batch_no || q.id}`}>
                                  <PencilSimple size={14} className="mr-2"/>Edit
                                </DropdownMenuItem>
                              )}
                              {q.result === "Passed" && !deleted && (
                                <DropdownMenuItem onSelect={() => setCertQc(q)} data-testid={`qc-cert-${q.batch_no || q.id}`}>
                                  <Certificate size={14} className="mr-2"/>Generate Certificate
                                </DropdownMenuItem>
                              )}
                              {canManage && (
                                <>
                                  <DropdownMenuSeparator/>
                                  {!deleted ? (
                                    <DropdownMenuItem className="text-red-600" onSelect={() => deleteQc(q)} data-testid={`qc-delete-${q.batch_no || q.id}`}>
                                      <Trash size={14} className="mr-2"/>Archive
                                    </DropdownMenuItem>
                                  ) : (
                                    <DropdownMenuItem className="text-emerald-700" onSelect={() => restoreQc(q)} data-testid={`qc-restore-${q.batch_no || q.id}`}>
                                      <ArrowUUpLeft size={14} className="mr-2"/>Restore
                                    </DropdownMenuItem>
                                  )}
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="templates">
          <div className="flex justify-end mb-3">
            {canManage && (
              <Button size="sm" onClick={() => { setTplSel(null); setTplMode("create"); }} data-testid="new-tpl-btn">
                <Plus size={14} className="mr-1"/>New Template
              </Button>
            )}
          </div>
          <Card className="shadow-none">
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead><TableHead>Stage</TableHead>
                    <TableHead className="text-right">Checkpoints</TableHead>
                    <TableHead>Applies to</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {templates.map(t => {
                    const del = t.status === "deleted";
                    const bound = t.sku_ids || (t.sku_id ? [t.sku_id] : []);
                    const boundLabels = bound.map(id => skus.find(s => s.id === id)?.code).filter(Boolean);
                    return (
                      <TableRow key={t.id} className={del ? "opacity-60" : ""} data-testid={`tpl-row-${t.name}`}>
                        <TableCell className="font-semibold">{t.name}</TableCell>
                        <TableCell>{t.stage}</TableCell>
                        <TableCell className="text-right mono">{t.checkpoints?.length || 0}</TableCell>
                        <TableCell className="text-xs">
                          {boundLabels.length === 0
                            ? <span className="text-muted-foreground">All SKUs</span>
                            : <div className="flex flex-wrap gap-1">{boundLabels.slice(0, 3).map(c => <Badge key={c} variant="secondary" className="mono text-[9px]">{c}</Badge>)}{boundLabels.length > 3 && <span className="text-muted-foreground text-[10px]">+{boundLabels.length - 3}</span>}</div>
                          }
                        </TableCell>
                        <TableCell>{del ? <Badge variant="secondary" className="uppercase text-[9px]">archived</Badge> : <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Active</Badge>}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{new Date(t.created_at).toLocaleDateString()}</TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button size="sm" variant="ghost" className="h-8 w-8 p-0" data-testid={`tpl-actions-${t.name}`}>
                                <DotsThreeVertical size={16} weight="bold"/>
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onSelect={() => { setTplSel(t); setTplMode("view"); }}>
                                <Eye size={14} className="mr-2"/>View
                              </DropdownMenuItem>
                              {canManage && !del && (
                                <DropdownMenuItem onSelect={() => { setTplSel(t); setTplMode("edit"); }} data-testid={`tpl-edit-${t.name}`}>
                                  <PencilSimple size={14} className="mr-2"/>Edit
                                </DropdownMenuItem>
                              )}
                              {canManage && (
                                <>
                                  <DropdownMenuSeparator/>
                                  {!del ? (
                                    <DropdownMenuItem className="text-red-600" onSelect={() => deleteTpl(t)} data-testid={`tpl-delete-${t.name}`}>
                                      <Trash size={14} className="mr-2"/>Archive
                                    </DropdownMenuItem>
                                  ) : (
                                    <DropdownMenuItem className="text-emerald-700" onSelect={() => restoreTpl(t)}>
                                      <ArrowUUpLeft size={14} className="mr-2"/>Restore
                                    </DropdownMenuItem>
                                  )}
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <QCRecordEditor open={!!qcMode} mode={qcMode} initial={qcSel} batches={batches}
                       templates={templates} onClose={() => { setQcMode(null); setQcSel(null); }} onDone={load}/>
      <TemplateEditor open={!!tplMode} mode={tplMode} initial={tplSel} skus={skus}
                       onClose={() => { setTplMode(null); setTplSel(null); }} onDone={load}/>
      <CertificateDialog open={!!certQc} qc={certQc} onClose={() => setCertQc(null)}/>
    </div>
  );
}
